import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { faImage } from '@fortawesome/pro-light-svg-icons';
import { useQueryClient } from '@tanstack/react-query';
import { AlertBanner, Badge, Button, List } from '@ygtechnologies/rubus-ui';
import { Toast } from '@ygtechnologies/bygr-frontend-component-library';

import { ConfiguratorGroupDto, ConfiguratorRoomDto, configuratorKeys, pickDefaults } from '@/services/configurator';
import { getProjectCatalogProductFileThumbnailUrl } from '@/services/project-catalog';

import { getRoomDisplayName } from '../../utils/configuratorRooms';
import { useOptionsData } from '../useOptionsData';
import theme from './PreSignCheck.module.scss';

interface Props {
    deadlineId: number;
    backToOptionsUrl: string;
    onCleared: () => void;
}

/** Groups in a room that the buyer still has to resolve before signing this deadline. */
interface RoomWithUnselected {
    roomId: number;
    roomName: string;
    unselectedGroups: ConfiguratorGroupDto[];
}

const isUnselected = (group: ConfiguratorGroupDto, deadlineId: number): boolean =>
    group.deadlineId === deadlineId &&
    group.applicable &&
    group.selectedOption === null &&
    !group.options.some((o) => o.selected);

const collectUnselected = (rooms: ConfiguratorRoomDto[], deadlineId: number): RoomWithUnselected[] =>
    rooms
        .map((room) => ({
            roomId: room.roomId,
            roomName: getRoomDisplayName(room),
            unselectedGroups: room.groups.filter((g) => isUnselected(g, deadlineId)),
        }))
        .filter((room) => room.unselectedGroups.length > 0);

const firstImageId = (images: { id: number; sortOrder: number }[] | undefined) =>
    images && images.length > 0 ? [...images].sort((a, b) => a.sortOrder - b.sortOrder)[0].id : null;

const UnselectedRow: React.FC<{ group: ConfiguratorGroupDto; roomName: string }> = ({ group, roomName }) => {
    const defaultOption = group.options.find((o) => o.isDefault);
    const imageId = firstImageId(defaultOption?.product?.images);
    const src = imageId != null ? getProjectCatalogProductFileThumbnailUrl(imageId) : undefined;
    return (
        <List.Item>
            <List.Thumbnail src={src} icon={faImage} size="md" alt={group.name} />
            <List.Content
                title={group.name}
                subtitle={
                    defaultOption ? (
                        <>
                            <Trans>Default:</Trans> <strong>{defaultOption.productName}</strong>
                        </>
                    ) : (
                        <span className={theme.noDefault}>
                            <Trans>No default — select manually</Trans>
                        </span>
                    )
                }
            />
            <List.Trailing>
                <Badge appearance="subtle">{roomName}</Badge>
            </List.Trailing>
        </List.Item>
    );
};

export const PreSignCheck: React.FC<Props> = ({ deadlineId, backToOptionsUrl, onCleared }) => {
    const { rooms, residentialUnitId, refetch } = useOptionsData();
    const queryClient = useQueryClient();
    const [loading, setLoading] = React.useState(false);

    const roomsWithUnselected = rooms ? collectUnselected(rooms, deadlineId) : [];
    const totalGroups =
        rooms?.reduce(
            (acc, room) => acc + room.groups.filter((g) => g.deadlineId === deadlineId && g.applicable).length,
            0
        ) ?? 0;
    const totalUnselected = roomsWithUnselected.reduce((acc, room) => acc + room.unselectedGroups.length, 0);

    // Everything is already selected — let the signing wizard take over.
    useEffect(() => {
        if (rooms && totalUnselected === 0) {
            onCleared();
        }
    }, [rooms, totalUnselected, onCleared]);

    const handleApplyDefaults = async () => {
        try {
            setLoading(true);
            const updatedData = await pickDefaults(residentialUnitId, deadlineId);

            const stillUnselected = updatedData.groups.filter((g) => isUnselected(g, deadlineId));

            queryClient.invalidateQueries({ queryKey: configuratorKeys.byUnit(residentialUnitId) });
            await refetch();

            if (stillUnselected.length > 0) {
                Toast.error(
                    t`${stillUnselected.length} option(s) have no default and must be selected manually before signing.`
                );
                setLoading(false);
                return;
            }

            onCleared();
        } catch {
            Toast.error(t`Something went wrong. Please try again.`);
            setLoading(false);
        }
    };

    // While auto-clearing (or before data loads) render nothing to avoid a flash.
    if (!rooms || totalUnselected === 0) {
        return null;
    }

    return (
        <div className={theme.container}>
            <AlertBanner type="warning">
                <strong>
                    <Trans>
                        {totalUnselected} out of {totalGroups} options do not have a selection.
                    </Trans>
                </strong>
                <p>
                    <Trans>
                        To continue signing, you need to make a selection for all options. You can apply the default
                        selection for all remaining options using the button below.
                    </Trans>
                </p>
            </AlertBanner>

            <List>
                {roomsWithUnselected.flatMap((room) =>
                    room.unselectedGroups.map((g) => (
                        <UnselectedRow key={`${g.id}:${g.roomId}`} group={g} roomName={room.roomName} />
                    ))
                )}
            </List>

            <div className={theme.buttons}>
                <Button as={Link} to={backToOptionsUrl} variant="tertiary">
                    <Trans>Cancel</Trans>
                </Button>
                <Button onClick={handleApplyDefaults} loading={loading}>
                    <Trans>Select default options for all</Trans>
                </Button>
            </div>
        </div>
    );
};
